import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { LOCATIONS, PLACES_BY_COUNTRY } from '../data/locations';
import { usePinHighlight, type HighlightOrigin } from '../state/pinHighlight';
import { focusRing } from './SiteHeader';

type Variant = Exclude<HighlightOrigin, 'pin'>;

const STYLES = {
  index: {
    group: 'border-white/[.075]',
    country: 'text-[10px] leading-10 pointer-coarse:leading-11 lg:pointer-fine:leading-6',
    countryIdle: 'text-white/50',
    countryActive: 'text-[#f2c66d]/90',
    row: 'h-10 pl-[18px] text-[15.5px] pointer-coarse:h-11 lg:text-[14.5px] lg:pointer-fine:h-6',
    rowIdle: 'text-white/75',
    rowActive: 'text-white',
    rowCurrent: 'text-white',
    rule: 'w-3',
    years: 'text-[10px]',
    yearsIdle: 'text-white/50',
    yearsActive: 'text-white/70 max-lg:hidden',
    yearsCurrent: 'text-white/70',
  },
  navigator: {
    group: 'border-white/[.06]',
    country: 'text-[9.5px] leading-[18px] pointer-coarse:leading-11',
    countryIdle: 'text-white/50',
    countryActive: 'text-white/70',
    row: 'h-[18px] pl-3.5 font-mono text-[10px] uppercase tracking-[0.12em] pointer-coarse:h-11',
    rowIdle: 'text-white/65',
    rowActive: 'text-white',
    rowCurrent: 'text-[#f2c66d]',
    rule: 'w-2',
    years: 'text-[9.5px]',
    yearsIdle: 'text-white/50',
    yearsActive: 'text-white/70',
    yearsCurrent: 'text-[#f2c66d]/75',
  },
} as const;

function PlaceList({ variant, current }: { variant: Variant; current?: string }) {
  const active = usePinHighlight((s) => s.slug);
  const pointer = useRef<{ type: string; wasActive: boolean } | null>(null);
  const s = STYLES[variant];
  const { activate, release } = usePinHighlight.getState();

  return (
    <ul className={variant === 'index' ? 'lg:border-t lg:border-white/[.075]' : undefined}>
      {[...PLACES_BY_COUNTRY].map(([country, cities]) => {
        const lit = cities.some((c) => c.id === active || c.id === current);
        return (
          <li
            key={country}
            className={`grid grid-cols-[92px_minmax(0,1fr)] border-t first:border-t-0 ${s.group}`}
          >
            <span
              aria-hidden="true"
              className={`whitespace-nowrap font-mono uppercase tracking-[0.14em] transition-colors duration-[140ms] ${s.country} ${lit ? s.countryActive : s.countryIdle}`}
            >
              {country}
            </span>
            <ul>
              {cities.map((city) => {
                const isCurrent = city.id === current;
                const isActive = city.id === active;
                const state = isCurrent ? 'Current' : isActive ? 'Active' : 'Idle';
                return (
                  <li key={city.id}>
                    <Link
                      to={`/city/${city.id}`}
                      replace={variant === 'navigator'}
                      aria-current={isCurrent ? 'page' : undefined}
                      aria-label={`${city.name}, ${country}, ${city.date}`}
                      onPointerEnter={(e) => {
                        if (e.pointerType !== 'touch') activate(`${variant}:pointer:${city.id}`, city.id, variant);
                      }}
                      onPointerLeave={() => release(`${variant}:pointer:${city.id}`)}
                      onFocus={() => activate(`${variant}:focus:${city.id}`, city.id, variant)}
                      onBlur={() => release(`${variant}:focus:${city.id}`)}
                      onPointerDown={(e) => {
                        pointer.current = { type: e.pointerType, wasActive: usePinHighlight.getState().slug === city.id };
                      }}
                      onClick={(e) => {
                        const down = pointer.current;
                        pointer.current = null;
                        if (isCurrent) {
                          e.preventDefault();
                          return;
                        }
                        if (variant !== 'index' || e.detail === 0 || !down || down.type === 'mouse' || down.wasActive)
                          return;
                        e.preventDefault();
                        activate('index:tap', city.id, 'index');
                      }}
                      className={`relative flex items-center justify-between gap-3 rounded-[3px] transition-colors duration-[140ms] ${s.row} ${s[`row${state}`]} ${focusRing} focus-visible:-outline-offset-2`}
                    >
                      <span
                        aria-hidden="true"
                        className={`absolute left-0 top-[calc(50%-0.75px)] h-[1.5px] origin-left bg-[#f2c66d] transition-transform duration-[140ms] ${s.rule} ${isCurrent || isActive ? 'scale-x-100' : 'scale-x-0'}`}
                      />
                      <span className="min-w-0 truncate">{city.name}</span>
                      <span
                        className={`shrink-0 font-mono leading-none tracking-[0.1em] tabular-nums transition-colors duration-[140ms] ${s.years} ${s[`years${state}`]}`}
                      >
                        {city.date}
                      </span>
                      {variant === 'index' && isActive && (
                        <span
                          aria-hidden="true"
                          className="shrink-0 font-mono text-[10px] uppercase leading-none tracking-[0.14em] text-[#f2c66d] lg:hidden"
                        >
                          Open →
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </li>
        );
      })}
    </ul>
  );
}

function useOverflowCue() {
  const ref = useRef<HTMLDivElement>(null);
  const [cue, setCue] = useState({ above: false, below: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const bottom = el.getBoundingClientRect().bottom;
      const below = [...el.querySelectorAll('a')].filter((a) => a.getBoundingClientRect().bottom > bottom + 0.5).length;
      const above = el.scrollTop > 0.5;
      setCue((c) => (c.above === above && c.below === below ? c : { above, below }));
    };
    const observer = new ResizeObserver(update);
    observer.observe(el);
    el.addEventListener('scroll', update, { passive: true });
    return () => {
      observer.disconnect();
      el.removeEventListener('scroll', update);
    };
  }, []);

  const attrs = { 'data-above': cue.above || undefined, 'data-below': cue.below > 0 || undefined };
  return { ref, attrs, below: cue.below };
}

function MoreCue({ count, className }: { count: number; className: string }) {
  if (count === 0) return null;
  return (
    <p
      aria-hidden="true"
      className={`pointer-events-none absolute bottom-0 w-[92px] bg-[#05070c] pt-1 font-mono text-[10px] uppercase leading-[15px] tracking-[0.14em] text-white/55 ${className}`}
    >
      ↓ {count} more
    </p>
  );
}

export function PlaceIndex() {
  const { ref, attrs, below } = useOverflowCue();

  return (
    <nav aria-label="Places" className="relative mt-1 lg:mt-3.5 lg:flex lg:min-h-0 lg:flex-1 lg:flex-col">
      <div
        ref={ref}
        {...attrs}
        className="scroll-cue [scrollbar-width:thin] lg:min-h-0 lg:flex-1 lg:scroll-pt-3 lg:scroll-pb-7 lg:overflow-y-auto"
      >
        <PlaceList variant="index" />
      </div>
      <MoreCue count={below} className="left-0" />
    </nav>
  );
}

export function PlaceNavigator({ current }: { current: string }) {
  const { ref, attrs, below } = useOverflowCue();

  useEffect(() => () => usePinHighlight.getState().clearAll(), []);

  return (
    <nav aria-label="All places" className="hidden min-h-0 flex-1 flex-col border-t border-white/10 lg:flex">
      <p className="flex justify-between px-6 pt-3.5 font-mono text-[10px] uppercase leading-[15px] tracking-[0.16em] text-white/50">
        <span>Index</span>
        <span>{LOCATIONS.length} places</span>
      </p>
      <div className="relative flex min-h-0 flex-1 flex-col">
        <div
          ref={ref}
          {...attrs}
          className="scroll-cue min-h-0 flex-1 scroll-pt-3 scroll-pb-7 overflow-y-auto px-6 pb-2 pt-2 [scrollbar-width:thin]"
        >
          <PlaceList variant="navigator" current={current} />
        </div>
        <MoreCue count={below} className="left-6" />
      </div>
    </nav>
  );
}
